import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays } from "lucide-react";

/* ─────────────── Seven Day Ticket Drawer ─────────────────────────────────── */
const TICKET_TYPE_DOT   = { client: "#6f42c1", alert: "#f59e0b", internal: "#8b5cf6" };
const TICKET_TYPE_LABEL = { client: "Client Created", alert: "Monitoring Alert", internal: "Internal" };

function sevenRelTime(isoStr) {
  if (!isoStr) return "—";
  const diff = Date.now() - new Date(isoStr).getTime();
  if (diff < 0) return "just now";
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function sevenStatusPill(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("resolv") || s.includes("complet")) return { bg: "#ecfdf5", color: "#059669", label: "Resolved"    };
  if (s.includes("waiting"))                          return { bg: "#fff5f5", color: "#dc2626", label: "Waiting"     };
  if (s.includes("progress") || s.includes("work"))  return { bg: "#fffbeb", color: "#d97706", label: "In Progress" };
  if (s.includes("new"))                              return { bg: "#eef2ff", color: "#6366f1", label: "New"         };
  return { bg: "#f8fafc", color: "#64748b", label: status || "Open" };
}

const SevenDayDrawer = ({ open, onClose, tickets = [] }) => {
  const [filter, setFilter]                 = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    if (!open) { setFilter("all"); setSelectedTicket(null); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        if (selectedTicket) setSelectedTicket(null);
        else onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, selectedTicket]);

  const counts = useMemo(() => ({
    all:      tickets.length,
    client:   tickets.filter(t => t.ticket_type === "client").length,
    alert:    tickets.filter(t => t.ticket_type === "alert").length,
    internal: tickets.filter(t => t.ticket_type === "internal").length,
  }), [tickets]);

  const filtered = useMemo(() =>
    filter === "all" ? tickets : tickets.filter(t => t.ticket_type === filter),
  [tickets, filter]);

  const PILLS = [
    { key: "all",      label: "All",               color: "#64748b", count: counts.all      },
    { key: "client",   label: "Client Created",    color: "#6f42c1", count: counts.client   },
    { key: "alert",    label: "Monitoring Alerts", color: "#f59e0b", count: counts.alert    },
    { key: "internal", label: "Internal",          color: "#8b5cf6", count: counts.internal },
  ];

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1060, backdropFilter: "blur(2px)" }} />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(640px, 96vw)",
        background: "var(--color-bg-card)",
        borderLeft: "1px solid var(--color-border)",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
        zIndex: 1070, display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(100,116,139,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CalendarDays size={18} color="#64748b" strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text)" }}>Opened in Last 7 Days</div>
            <div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 2 }}>{counts.all} ticket{counts.all !== 1 ? "s" : ""} — click a row to view details</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(100,116,139,0.1)", border: "none", cursor: "pointer", color: "var(--color-text-light)", padding: "7px 8px", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Filter pills */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--color-border)", display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
          {PILLS.map(pill => {
            const active = filter === pill.key;
            return (
              <button key={pill.key} onClick={() => { setFilter(pill.key); setSelectedTicket(null); }}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20,
                  border: `1px solid ${active ? pill.color : "rgba(100,116,139,0.2)"}`,
                  background: active ? `${pill.color}18` : "transparent",
                  color: active ? pill.color : "var(--color-text-light)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                }}>
                {pill.key !== "all" && <span style={{ width: 7, height: 7, borderRadius: "50%", background: pill.color, flexShrink: 0, display: "inline-block" }} />}
                {pill.label}
                <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 6px", lineHeight: 1.5, background: active ? pill.color : "rgba(100,116,139,0.15)", color: active ? "#fff" : "var(--color-text-light)" }}>{pill.count}</span>
              </button>
            );
          })}
        </div>

        {/* Ticket list + nested detail panel */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <div style={{ height: "100%", overflowY: selectedTicket ? "hidden" : "auto", overscrollBehavior: "contain", scrollbarWidth: "thin", scrollbarColor: "#d1d5db transparent" }}>
            {/* Column headers */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 20px", borderBottom: "1px solid #f3f4f6", borderLeft: "3px solid transparent", background: "var(--color-bg-card)", position: "sticky", top: 0, zIndex: 2 }}>
              <div style={{ width: 8, flexShrink: 0 }} />
              <span style={{ fontFamily: "monospace", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-text-light)", flexShrink: 0, minWidth: 90 }}>Ticket ID</span>
              <span style={{ flex: 1, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-text-light)" }}>Title</span>
              {!selectedTicket && <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-text-light)", flexShrink: 0 }}>Status</span>}
              {!selectedTicket && <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-text-light)", flexShrink: 0, minWidth: 60, textAlign: "right" }}>Opened</span>}
              <div style={{ width: 14, flexShrink: 0 }} />
            </div>
            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-text-light)", fontSize: 13 }}>No tickets found for this filter</div>
            ) : filtered.map((t) => {
              const pill   = sevenStatusPill(t.status);
              const active = selectedTicket?.ticket_id === t.ticket_id;
              return (
                <button key={t.ticket_id} type="button"
                  onClick={() => setSelectedTicket(active ? null : t)}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(100,116,139,0.04)"; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? "rgba(111,66,193,0.06)" : "transparent"; }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "15px 20px",
                    width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid #f3f4f6",
                    borderLeft: active ? "3px solid #6f42c1" : "3px solid transparent",
                    cursor: "pointer", transition: "background 0.12s, border-left 0.15s",
                    background: active ? "rgba(111,66,193,0.06)" : "transparent",
                    font: "inherit", color: "inherit",
                  }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: TICKET_TYPE_DOT[t.ticket_type] || "#64748b", flexShrink: 0 }} />
                  <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", flexShrink: 0, minWidth: 90 }}>{t.ticket_number}</span>
                  <span style={{ flex: 1, fontSize: 13, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{t.title || "—"}</span>
                  {!selectedTicket && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", background: pill.bg, color: pill.color, padding: "3px 8px", borderRadius: 10, flexShrink: 0 }}>{pill.label}</span>}
                  {!selectedTicket && <span style={{ fontSize: 11, color: "var(--color-text-light)", flexShrink: 0, minWidth: 60, textAlign: "right" }}>{sevenRelTime(t.created_date)}</span>}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-light)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.5 }}><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              );
            })}
          </div>

          {/* Detail panel — slides in from right, scoped within the drawer */}
          <AnimatePresence>
            {selectedTicket && (
              <motion.div key="7day-detail"
                initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 280 }}
                style={{
                  position: "absolute", top: 0, right: 0, bottom: 0, width: "40%",
                  background: "var(--color-bg-card)", borderLeft: "1px solid var(--color-border)",
                  boxShadow: "-8px 0 24px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", zIndex: 5,
                }}>
                <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: TICKET_TYPE_DOT[selectedTicket.ticket_type] || "#64748b", flexShrink: 0 }} />
                      <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "var(--color-text-light)" }}>{selectedTicket.ticket_number}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)", lineHeight: 1.4, wordBreak: "break-word" }}>{selectedTicket.title || "Untitled Ticket"}</div>
                  </div>
                  <button onClick={() => setSelectedTicket(null)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--color-text-light)", flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <div style={{ padding: "8px 18px", flex: 1, overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: "#d1d5db transparent" }}>
                  {[
                    ["Type",     TICKET_TYPE_LABEL[selectedTicket.ticket_type] || selectedTicket.ticket_type],
                    ["Status",   selectedTicket.status   || "—"],
                    ["Priority", selectedTicket.priority || "—"],
                    ["Queue",    selectedTicket.queue    || "—"],
                    ["Created",  selectedTicket.created_date ? new Date(selectedTicket.created_date).toLocaleString() : "—"],
                  ].map(([label, val]) => {
                    const isStatus = label === "Status";
                    const sp = isStatus ? sevenStatusPill(val) : null;
                    return (
                      <div key={label} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "7px 0", borderBottom: "1px solid #f9fafb" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: "#9ca3af" }}>{label}</span>
                        {isStatus
                          ? <span style={{ display: "inline-flex", alignSelf: "flex-start", fontSize: 11, fontWeight: 700, letterSpacing: "0.3px", background: sp.bg, color: sp.color, padding: "3px 9px", borderRadius: 10 }}>{sp.label}</span>
                          : <span style={{ fontSize: 13, color: "var(--color-text)", wordBreak: "break-word", lineHeight: 1.4 }}>{val}</span>
                        }
                      </div>
                    );
                  })}
                </div>
                <div style={{ padding: "14px 18px", borderTop: "1px solid var(--color-border)", flexShrink: 0 }}>
                  <a href={`/api/autotask/redirect/${selectedTicket.ticket_id}/`} target="_blank" rel="noopener noreferrer"
                    style={{ display: "block", textAlign: "center", background: "#6f42c1", color: "#fff", padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: "none", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#5a32a3"}
                    onMouseLeave={e => e.currentTarget.style.background = "#6f42c1"}>
                    Open full ticket ↗
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default SevenDayDrawer;
